export interface ShippingOption {
  /**
   * ShippingOption ID.
   * @format GUID
   * @readonly
   */
  id?: string | null;
  /**
   * Revision number, which increments by 1 each time the ShippingOption is updated.
   * To prevent conflicting changes,
   * the current revision must be passed when updating the ShippingOption.
   *
   * Ignored when creating a ShippingOption.
   * @readonly
   */
  revision?: string | null;
  /**
   * Date and time the ShippingOption was created.
   * @readonly
   */
  createdDate?: Date | null;
  /**
   * Date and time the ShippingOption was last updated.
   * @readonly
   */
  updatedDate?: Date | null;
  /**
   * Associated delivery region id.
   * @format GUID
   * @immutable
   * @deprecated Associated delivery region id.
   * @replacedBy delivery_region_ids
   * @targetRemovalDate 2025-06-01
   */
  deliveryRegionId?: string;
  /**
   * This shipping option is active for the following delivery regions.
   * @maxSize 50
   * @format GUID
   * @immutable
   */
  deliveryRegionIds?: string[];
  /**
   * Delivery option configured for the ShippingOption
   * @maxLength 1000
   */
  title?: string | null;
  /**
   * Estimated delivery time. For example, "2-3 business days".
   * @maxLength 500
   */
  estimatedDeliveryTime?: string | null;
  /**
   * at runtime for a given shipment input, up to one rate (price) should be returned in an option. If more than one rate is valid then we return the lowest one.
   * @minSize 1
   * @maxSize 50
   */
  rates?: ConditionalRates[];
  /** Data Extensions */
  extendedFields?: ExtendedFields;
}

export interface ConditionalRates {
  /**
   * there is an AND logic between all the conditions. Empty conditions means true.
   * The amount of the rate that will be returned if all conditions are met.
   * @decimalValue options { gte:0, lte:999999999, maxScale:3 }
   */
  amount?: string;
  /**
   * For example: weight > 0 and weight <= 10
   * @maxSize 10
   */
  conditions?: Condition[];
  /** When this flag is set to true, multiply the amount by the number of line items passed on the request. */
  multiplyByQuantity?: boolean;
}

export interface Condition {
  type?: ConditionTypeWithLiterals;
  /**
   * The value in respective to the condition type
   * Weight values should be in the same weight units of the store: KG / LB
   * Total price is according to the store currency
   * Quantity of items should be integers
   * @decimalValue options { gte:0, lte:999999999, maxScale:6 }
   */
  value?: string;
  /** Logical operator. */
  operator?: LogicalOperatorWithLiterals;
}

export enum ConditionType {
  BY_TOTAL_WEIGHT = 'BY_TOTAL_WEIGHT',
  BY_TOTAL_PRICE = 'BY_TOTAL_PRICE',
  BY_TOTAL_QUANTITY = 'BY_TOTAL_QUANTITY',
}

/** @enumType */
export type ConditionTypeWithLiterals =
  | ConditionType
  | 'BY_TOTAL_WEIGHT'
  | 'BY_TOTAL_PRICE'
  | 'BY_TOTAL_QUANTITY';

export enum LogicalOperator {
  EQ = 'EQ',
  GT = 'GT',
  GTE = 'GTE',
  LT = 'LT',
  LTE = 'LTE',
}

/** @enumType */
export type LogicalOperatorWithLiterals =
  | LogicalOperator
  | 'EQ'
  | 'GT'
  | 'GTE'
  | 'LT'
  | 'LTE';

export interface ExtendedFields {
  /**
   * Extended field data. Each key corresponds to the namespace of the app that created the extended fields.
   * The value of each key is structured according to the schema defined when the extended fields were configured.
   *
   * You can only access fields for which you have the appropriate permissions.
   *
   * Learn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).
   */
  namespaces?: Record<string, Record<string, any>>;
}

export interface CreateShippingOptionRequest {
  /** ShippingOption to be created. */
  shippingOption: ShippingOption;
}

export interface CreateShippingOptionResponse {
  /** The created ShippingOption. */
  shippingOption?: ShippingOption;
}

export interface GetShippingOptionRequest {
  /**
   * ID of the ShippingOption to retrieve.
   * @format GUID
   */
  shippingOptionId: string;
}

export interface GetShippingOptionResponse {
  /** The requested ShippingOption. */
  shippingOption?: ShippingOption;
}

export interface UpdateShippingOptionRequest {
  /** ShippingOption to be updated, may be partial. */
  shippingOption: ShippingOption;
  /**
   * Set of fields to update.
   *
   * Fields that aren't included in `fieldMask.paths` are ignored.
   * @internal
   */
  fieldMask?: string[];
}

export interface UpdateShippingOptionResponse {
  /** Updated ShippingOption. */
  shippingOption?: ShippingOption;
}

export interface DeleteShippingOptionRequest {
  /**
   * Id of the ShippingOption to delete.
   * @format GUID
   */
  shippingOptionId: string;
}

export interface DeleteShippingOptionResponse {}

export interface QueryShippingOptionsRequest {
  /** WQL expression. */
  query?: CursorQuery;
}

export interface CursorQuery extends CursorQueryPagingMethodOneOf {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
  /**
   * Filter object in the following format:
   * `"filter" : {
   * "fieldName1": "value1",
   * "fieldName2":{"$operator":"value2"}
   * }`
   * Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`
   */
  filter?: Record<string, any> | null;
  /**
   * Sort object in the following format:
   * `[{"fieldName":"sortField1","order":"ASC"},{"fieldName":"sortField2","order":"DESC"}]`
   * @maxSize 5
   */
  sort?: Sorting[];
}

/** @oneof */
export interface CursorQueryPagingMethodOneOf {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
}

export interface Sorting {
  /**
   * Name of the field to sort by.
   * @maxLength 512
   */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface CursorPaging {
  /**
   * Maximum number of items to return in the results.
   * @max 100
   */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   * @maxLength 16000
   */
  cursor?: string | null;
}

export interface QueryShippingOptionsResponse {
  /**
   * List of ShippingOptions.
   * @maxSize 100
   */
  shippingOptions?: ShippingOption[];
  /** Paging metadata */
  pagingMetadata?: CursorPagingMetadata;
}

export interface CursorPagingMetadata {
  /** Number of items returned in the response. */
  count?: number | null;
  /** Cursor strings that point to the next page, previous page, or both. */
  cursors?: Cursors;
  /**
   * Whether there are more pages to retrieve following the current page.
   *
   * + `true`: Another page of results can be retrieved.
   * + `false`: This is the last page.
   */
  hasNext?: boolean | null;
}

export interface Cursors {
  /**
   * Cursor string pointing to the next page in the list of results.
   * @maxLength 16000
   */
  next?: string | null;
  /**
   * Cursor pointing to the previous page in the list of results.
   * @maxLength 16000
   */
  prev?: string | null;
}

export interface UpdateExtendedFieldsRequest {
  /** ID of the entity to update. */
  id: string;
  /** Identifier for the app whose extended fields are being updated. */
  namespace: string;
  /** Data to update. Structured according to the [schema](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields#json-schema-for-extended-fields) defined when the extended fields were configured. */
  namespaceData: Record<string, any> | null;
}

export interface UpdateExtendedFieldsResponse {
  /** Updated ShippingOption. */
  shippingOption?: ShippingOption;
}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at `16:00` and then again at `16:01`, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entityAsJson?: string;
  /**
   * Indicates the event was triggered by a restore-from-trashbin operation for a previously deleted entity
   * @internal
   */
  triggeredByUndelete?: boolean | null;
  /** Indicates the event was triggered by a restore-from-trashbin operation for a previously deleted entity */
  restoreInfo?: RestoreInfo;
  /**
   * Optional domain-specific metadata defined by the API owner, encoded as JSON.
   * @internal
   */
  additionalMetadataAsJson?: string | null;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntityAsJson?: string;
  /**
   * This field is currently part of the of the EntityUpdatedEvent msg, but scala/node libraries which implements the domain events standard
   * wont populate it / have any reference to it in the API.
   * The main reason for it is that fetching the old entity from the DB will have a performance hit on an update operation so unless truly needed,
   * the developer should send only the new (current) entity.
   * An additional reason is not wanting to send this additional entity over the wire (kafka) since in some cases it can be really big
   * Developers that must reflect the old entity will have to implement their own domain event sender mechanism which will follow the DomainEvent proto message.
   * @internal
   * @deprecated
   */
  previousEntityAsJson?: string | null;
  /**
   * Map of fully qualified field paths to their previous values for fields that changed.
   * For more details please see [AIP](https://dev.wix.com/docs/rnd-general/articles/p13n-guidelines-aips/guidance-aips/design-patterns/7016-events#modified-fields-format)
   * @internal
   */
  modifiedFields?: Record<string, any>;
  /**
   * Optional domain-specific metadata defined by the API owner, encoded as JSON.
   * @internal
   */
  additionalMetadataAsJson?: string | null;
}

export interface EntityDeletedEvent {
  /**
   * Indicates if the entity is sent to trash-bin. only available when trash-bin is enabled
   * @internal
   */
  movedToTrash?: boolean | null;
  /** Entity that was deleted. */
  deletedEntityAsJson?: string | null;
  /**
   * Optional domain-specific metadata defined by the API owner, encoded as JSON.
   * @internal
   */
  additionalMetadataAsJson?: string | null;
}

export interface ActionEvent {
  bodyAsJson?: string;
}

export interface Empty {}

export interface AddDeliveryRegionRequest {
  /**
   * ID of the delivery region that should use this ShippingOption.
   * @format GUID
   */
  deliveryRegionId: string;
  /**
   * ID of the ShippingOption to be updated.
   * @format GUID
   */
  shippingOptionId: string;
  /** Revision of the ShippingOption. */
  revision: string | null;
}

export interface AddDeliveryRegionResponse {
  /** Updated ShippingOption. */
  shippingOption?: ShippingOption;
}

export interface RemoveDeliveryRegionRequest {
  /**
   * ID of the delivery region that should not use this ShippingOption anymore.
   * @format GUID
   */
  deliveryRegionId: string;
  /**
   * ID of the ShippingOption to be updated.
   * @format GUID
   */
  shippingOptionId: string;
  /** Revision of the ShippingOption. */
  revision: string | null;
}

export interface RemoveDeliveryRegionResponse {
  /** Updated ShippingOption. */
  shippingOption?: ShippingOption;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

/** @docsIgnore */
export type CreateShippingOptionApplicationErrors = {
  code?: 'INVALID_QUANTITY_VALUE';
  description?: string;
  data?: Record<string, any>;
};
/** @docsIgnore */
export type UpdateShippingOptionApplicationErrors = {
  code?: 'INVALID_QUANTITY_VALUE';
  description?: string;
  data?: Record<string, any>;
};
